import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MainLayoutContext } from "@/app/layouts/MainLayout";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Inbox,
  Loader2,
  RefreshCcw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient, http, parseApiError } from "@/app/services/api";
import {
  useFormatDate,
  useFormatUiDateTime,
  useFormatUiDateTimeWithSeconds,
} from "@/app/utils/format";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label, RequiredLabel } from "@/app/components/ui/label";
import { FieldHint } from "@/app/components/ui/field-hint";
import { Badge } from "@/app/components/ui/badge";
import { Checkbox } from "@/app/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/app/components/ui/radio-group";
import { Select } from "@/app/components/ui/select";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { LoadingState, EmptyState, ErrorState } from "@/app/components/ui/empty-state";
import { Table, TBody, TD, TH, THead, TR } from "@/app/components/ui/table";
import {
  DataPagination,
  DEFAULT_PAGE_SIZE,
  usePagedItems,
} from "@/app/components/ui/data-pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/components/ui/alert-dialog";
import { DismissRequestDialog, RevokeCoreDialog } from "@/app/pages/ConfirmPageDialogs";
import type {
  Tenant,
  TenantSub,
  EntitlementRequest,
  CoreInstance,
} from "@/app/types/api";

const STATUS_OPTIONS = [
  "all",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "expired",
] as const;

const REQUEST_STATUS_OPTIONS = ["all", "pending", "resolved"] as const;
const REQUEST_PAGE_SIZE_OPTIONS = [3, 6, 10] as const;
const REQUEST_DEFAULT_PAGE_SIZE = 3;

type PlanCatalogItem = {
  code: string;
  name: string | null;
  app_ids: string[];
};

type ConfirmEntitlementResult = {
  status?: "confirmed" | "already";
  tenant_id?: string;
  subscription_id?: string;
  target_type?: "plan" | "app";
  target_id?: string;
  expires_at?: string;
};

function resolveSingleAppPlan(plans: PlanCatalogItem[], appId: string) {
  const candidates = plans.filter(
    (plan) => plan.app_ids.length === 1 && plan.app_ids[0] === appId
  );
  return candidates.length === 1 ? candidates[0] : null;
}

async function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export function ConfirmPage() {
  const activation = useActivationCodeForm();
  const manual = useManualConfirmForm();
  const [activationOpen, setActivationOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);
  const { t } = useTranslation();
  const { setActions } = useOutletContext<MainLayoutContext>();

  React.useEffect(() => {
    setActions(
      <>
        <Button size="sm" onClick={() => setActivationOpen(true)}>
          {t("activation.submit")}
        </Button>
        <Button size="sm" onClick={() => setManualOpen(true)}>
          {t("confirm.submit")}
        </Button>
      </>
    );
    return () => setActions(null);
  }, [setActions, t]);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="grid flex-1 min-h-0 grid-rows-[auto_minmax(0,1fr)] items-stretch gap-4 has-[[data-requests-expanded=true]]:grid-rows-[minmax(22rem,2fr)_minmax(0,3fr)]">
        <div className="flex min-h-0">
          <RequestsCard />
        </div>
        <div className="flex min-h-0">
          <TenantsAndInstancesCard />
        </div>
      </div>
      <ActivationCodeDialog
        open={activationOpen}
        onOpenChange={setActivationOpen}
        form={activation}
      />
      <ManualConfirmDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        form={manual}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const variant: "success" | "warning" | "danger" | "default" =
    status === "active" || status === "trialing"
      ? "success"
      : status === "past_due"
        ? "warning"
        : status === "canceled" || status === "expired"
          ? "danger"
          : "default";
  return <Badge variant={variant}>{t(statusLabelKey(status))}</Badge>;
}

function statusLabelKey(status: string) {
  switch (status) {
    case "trialing":
      return "tenantSubs.status.trialing";
    case "active":
      return "tenantSubs.status.active";
    case "past_due":
      return "tenantSubs.status.pastDue";
    case "canceled":
      return "tenantSubs.status.canceled";
    case "expired":
      return "tenantSubs.status.expired";
    default:
      return "tenantSubs.status.unknown";
  }
}

function RequestStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const variant: "warning" | "success" | "default" =
    status === "pending" ? "warning" : status === "resolved" ? "success" : "default";
  return <Badge variant={variant}>{t(requestStatusLabelKey(status))}</Badge>;
}

function requestStatusLabelKey(status: string) {
  switch (status) {
    case "pending":
      return "requests.status.pending";
    case "resolved":
      return "requests.status.resolved";
    default:
      return "requests.status.unknown";
  }
}

function RowDetailsToggle({
  open,
  onClick,
  label,
}: {
  open: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
    >
      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      {label}
    </button>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-[0.02em] text-muted-foreground">
        {label}
      </div>
      <div className="text-xs text-foreground">{value}</div>
    </div>
  );
}

function ActivationResultRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
      <span className="shrink-0 font-medium text-emerald-950">{label}:</span>
      <code className="break-all rounded bg-white/70 px-1.5 py-0.5 font-mono text-xs text-emerald-950 ring-1 ring-emerald-200">
        {value}
      </code>
    </div>
  );
}

function TenantsAndInstancesCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = React.useState<"tenantSubs" | "coreInstances">(
    "tenantSubs"
  );

  return (
    <Card className="flex h-full w-full flex-col">
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as typeof activeTab)}
        className="flex-1 min-h-0"
      >
        <CardHeader>
          <TabsList>
            <TabsTrigger value="tenantSubs">{t("tenantSubs.title")}</TabsTrigger>
            <TabsTrigger value="coreInstances">{t("coreInstances.title")}</TabsTrigger>
          </TabsList>
          <CardAction>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                qc.invalidateQueries({
                  queryKey:
                    activeTab === "tenantSubs"
                      ? ["admin", "tenant-subs"]
                      : ["admin", "core-instances"],
                })
              }
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              {t(activeTab === "tenantSubs" ? "tenantSubs.reload" : "coreInstances.reload")}
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="flex h-full min-h-0 flex-col">
          <TabsContent value="tenantSubs" className="flex flex-1 min-h-0 flex-col">
            <TenantSubsPanel />
          </TabsContent>
          <TabsContent value="coreInstances" className="flex flex-1 min-h-0 flex-col">
            <CoreInstancesPanel />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}

function TenantSubsPanel() {
  const { t } = useTranslation();
  const fmt = useFormatDate();
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState<(typeof STATUS_OPTIONS)[number]>("all");
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);

  const list = useQuery({
    queryKey: ["admin", "tenant-subs", q, status],
    queryFn: () => {
      const p: Record<string, string> = { limit: "300" };
      if (q) p.q = q;
      if (status) p.status = status;
      return http.get<{ items: TenantSub[] }>(
        "/api/v1/admin/tenant-subscriptions",
        p
      );
    },
  });

  const items = list.data?.items ?? [];
  const { page, setPage, totalPages, pageItems } = usePagedItems(items, pageSize);
  React.useEffect(() => {
    setPage(1);
  }, [q, status, pageSize, setPage]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") list.refetch();
            }}
            placeholder={t("tenantSubs.search")}
            className="pl-7 h-8 w-56 text-xs"
          />
        </div>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="h-8 text-xs w-36"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? t("tenantSubs.status.all") : t(statusLabelKey(s))}
            </option>
          ))}
        </Select>
      </div>
      {list.isLoading ? (
  <LoadingState />
) : list.isError ? (
  <ErrorState message={t("tenantSubs.loadError")} onRetry={() => list.refetch()} />
) : items.length === 0 ? (
  <EmptyState icon={Inbox} message={t("tenantSubs.empty")} />
) : (
  <>
  <div className="flex-1 min-h-0 overflow-auto">
  <Table>
            <THead>
              <TR>
                <TH>{t("tenantSubs.col.tenant")}</TH>
                <TH>{t("tenantSubs.col.target")}</TH>
                <TH>{t("tenantSubs.col.status")}</TH>
                <TH>{t("tenantSubs.col.period")}</TH>
                <TH>{t("tenantSubs.col.expiry")}</TH>
                <TH>{t("tenantSubs.col.seats")}</TH>
                <TH>{t("tenantSubs.col.order")}</TH>
              </TR>
            </THead>
            <TBody>
              {pageItems.map((r) => {
                const targetKind = r.target_type === "plan" ? t("tenantSubs.target.plan") : t("tenantSubs.target.app");
                const remaining = Number(r.remaining_days);
                const expiryNote = remaining >= 0 ? t("tenantSubs.remaining", { days: remaining }) : t("tenantSubs.expired");
                return (
                  <TR key={r.subscription_id}>
                    <TD>
                      <div className="font-medium">{r.tenant_code}</div>
                      {r.tenant_name && r.tenant_name !== r.tenant_code && (
                        <div className="text-xs text-muted-foreground">{r.tenant_name}</div>
                      )}
                    </TD>
                    <TD>
                      <div className="text-xs">
                        {targetKind}: <span className="font-mono">{r.target_id}</span>
                      </div>
                      {r.target_name && r.target_name !== r.target_id && (
                        <div className="text-xs text-muted-foreground">{r.target_name}</div>
                      )}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap gap-1">
                        <StatusBadge status={r.status} />
                        {r.cancel_at_period_end && (
                          <Badge variant="warning">{t("tenantSubs.cancelAtPeriodEnd")}</Badge>
                        )}
                      </div>
                    </TD>
                    <TD>
                      <div className="text-xs">
                        {fmt(r.current_period_start)} → {fmt(r.current_period_end)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {(r.billing_interval || "—") + " · " + t("tenantSubs.periodDays", { days: r.period_days })}
                      </div>
                    </TD>
                    <TD>
                      <div className="text-xs">{fmt(r.current_period_end)}</div>
                      <div className="text-xs text-muted-foreground">{expiryNote}</div>
                    </TD>
                    <TD>{r.seat_count == null ? t("tenantSubs.unlimited") : r.seat_count}</TD>
                    <TD className="font-mono text-xs">{r.order_ref || "—"}</TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
  </div>
  <DataPagination
    page={page}
    totalPages={totalPages}
	    pageSize={pageSize}
	    onChange={setPage}
	    onPageSizeChange={setPageSize}
	  />
  </>
)}
    </div>
  );
}

function RequestsCard() {
  const { t } = useTranslation();
  const fmt = useFormatUiDateTimeWithSeconds();
  const qc = useQueryClient();
  const [tableOpen, setTableOpen] = React.useState(true);
  const [status, setStatus] = React.useState<(typeof REQUEST_STATUS_OPTIONS)[number]>("all");
  const [pageSize, setPageSize] = React.useState<number>(REQUEST_DEFAULT_PAGE_SIZE);
  const [timeSort, setTimeSort] = React.useState<"asc" | "desc">("desc");
  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>({});
  const list = useQuery({
    queryKey: ["admin", "requests", status],
    queryFn: () =>
      http.get<{ items: EntitlementRequest[] }>("/api/v1/entitlements/requests", {
        status,
      }),
  });

  const dismiss = useMutation({
    mutationFn: async (input: { id: string }) => {
      await apiClient.post(
        `/api/v1/entitlements/requests/${encodeURIComponent(input.id)}/dismiss`,
        { status: "resolved" }
      );
    },
    onSuccess: () => {
      toast.success(t("requests.dismissSuccess"));
      qc.invalidateQueries({ queryKey: ["admin", "requests"] });
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  const [dismissTarget, setDismissTarget] = React.useState<string | null>(null);

  const items = React.useMemo(() => {
    const rawItems = list.data?.items ?? [];
    return rawItems.slice().sort((a, b) => {
      const aTime = new Date(a.requested_at).getTime();
      const bTime = new Date(b.requested_at).getTime();
      const diff = (Number.isFinite(aTime) ? aTime : 0) - (Number.isFinite(bTime) ? bTime : 0);
      return timeSort === "asc" ? diff : -diff;
    });
  }, [list.data?.items, timeSort]);
  const { page, setPage, totalPages, pageItems } = usePagedItems(items, pageSize);
  React.useEffect(() => {
    setPage(1);
  }, [status, pageSize, setPage]);

  return (
    <Card
      className={tableOpen ? "flex h-full w-full flex-col gap-0" : "flex w-full flex-col gap-0"}
      data-requests-expanded={tableOpen}
    >
      <CardHeader className={tableOpen ? undefined : "py-4"}>
        <CardTitle>
          <button
            type="button"
            onClick={() => setTableOpen((prev) => !prev)}
            className="inline-flex items-center gap-2 text-left transition-colors hover:text-primary"
            aria-expanded={tableOpen}
            aria-controls="requests-table-region"
          >
            {tableOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {t("requests.title")}
          </button>
        </CardTitle>
        {tableOpen && (
        <CardAction>
          <div className="flex flex-wrap justify-end gap-2">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="h-8 w-32 text-xs"
              aria-label={t("requests.statusFilter")}
            >
              {REQUEST_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? t("requests.status.all") : t(requestStatusLabelKey(s))}
                </option>
              ))}
            </Select>
            <Button size="sm" variant="outline" onClick={() => list.refetch()} disabled={list.isFetching}>
              <RefreshCcw className="h-3.5 w-3.5" />
              {t("requests.reload")}
            </Button>
          </div>
        </CardAction>
        )}
      </CardHeader>
      {tableOpen && (
      <CardContent id="requests-table-region" className="flex flex-1 min-h-0 flex-col gap-2">
        {list.isLoading ? (
  <LoadingState />
) : list.isError ? (
  <ErrorState message={t("requests.loadError")} onRetry={() => list.refetch()} />
) : items.length === 0 ? (
  <EmptyState icon={Inbox} message={t("requests.empty")} />
) : (
  <>
  <div className="flex-1 min-h-0 overflow-auto">
  <Table>
            <THead>
              <TR>
                <TH>{t("requests.col.tenant")}</TH>
                <TH>{t("requests.col.target")}</TH>
                <TH>{t("requests.col.type")}</TH>
                <TH>{t("requests.col.seats")}</TH>
                <TH>{t("requests.col.note")}</TH>
                <TH aria-sort={timeSort === "asc" ? "ascending" : "descending"}>
                  <button
                    type="button"
                    onClick={() => setTimeSort((prev) => (prev === "asc" ? "desc" : "asc"))}
                    className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                  >
                    {t("requests.col.requestedAt")}
                    <span aria-hidden="true">{timeSort === "asc" ? "↑" : "↓"}</span>
                  </button>
                </TH>
                <TH></TH>
              </TR>
            </THead>
            <TBody>
              {pageItems.map((r) => {
                const expanded = !!expandedRows[r.id];
                return (
                  <React.Fragment key={r.id}>
                    <TR>
                      <TD className="font-mono text-xs">
                        <div>{r.tenant_code}</div>
                        <div className="mt-1">
                          <RowDetailsToggle
                            open={expanded}
                            onClick={() =>
                              setExpandedRows((prev) => ({
                                ...prev,
                                [r.id]: !prev[r.id],
                              }))
                            }
                            label={t(expanded ? "table.hideDetails" : "table.showDetails")}
                          />
                        </div>
                      </TD>
                      <TD>
                        <div className="text-xs">
                          {(r.target_type === "plan" ? t("confirm.target.plan") : t("confirm.target.app")) + ": "}
                          <span className="font-mono">{r.target_id}</span>
                        </div>
                      </TD>
                      <TD>
                        <div className="flex flex-wrap gap-1">
                          {r.is_renewal ? (
                            <Badge variant="primary">{t("requests.renewal")}</Badge>
                          ) : (
                            <Badge variant="outline">{t("requests.new")}</Badge>
                          )}
                          <RequestStatusBadge status={r.status} />
                        </div>
                      </TD>
                      <TD>{r.seat_count ?? "—"}</TD>
                      <TD className="text-xs">{r.note || "—"}</TD>
                      <TD className="text-xs">{fmt(r.requested_at)}</TD>
                      <TD>
                        {r.status === "pending" ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDismissTarget(r.id)}
                          >
                            {t("requests.dismiss")}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t(requestStatusLabelKey(r.status))}
                          </span>
                        )}
                      </TD>
                    </TR>
                    {expanded && (
                      <TR className="bg-muted/20 hover:bg-muted/20">
                        <TD colSpan={7} className="px-3 py-3">
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <DetailItem
                              label={t("requests.detail.status")}
                              value={t(requestStatusLabelKey(r.status))}
                            />
                            <DetailItem
                              label={t("requests.detail.resolvedAt")}
                              value={r.resolved_at ? fmt(r.resolved_at) : "—"}
                            />
                            <DetailItem
                              label={t("requests.detail.resolvedBy")}
                              value={r.resolved_by || "—"}
                            />
                          </div>
                        </TD>
                      </TR>
                    )}
                  </React.Fragment>
                );
              })}
            </TBody>
          </Table>
  </div>
  <DataPagination
    page={page}
    totalPages={totalPages}
	    pageSize={pageSize}
	    onChange={setPage}
	    onPageSizeChange={setPageSize}
	    pageSizeOptions={REQUEST_PAGE_SIZE_OPTIONS}
	  />
  </>
)}
      </CardContent>
      )}
      <DismissRequestDialog
        id={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onSubmit={async () => {
          if (!dismissTarget) return;
          await dismiss.mutateAsync({ id: dismissTarget });
          setDismissTarget(null);
        }}
        isPending={dismiss.isPending}
      />
    </Card>
  );
}

function CoreInstancesPanel() {
  const { t } = useTranslation();
  const fmt = useFormatUiDateTime();
  const qc = useQueryClient();
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
  const list = useQuery({
    queryKey: ["admin", "core-instances"],
    queryFn: () =>
      http.get<{ items: CoreInstance[] }>("/api/v1/admin/core-instances"),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(
        `/api/v1/admin/core-instances/${encodeURIComponent(id)}/revoke`
      );
    },
    onSuccess: () => {
      toast.success(t("coreInstances.revokeSuccess"));
      qc.invalidateQueries({ queryKey: ["admin", "core-instances"] });
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  const [revokeTarget, setRevokeTarget] = React.useState<string | null>(null);

  const items = list.data?.items ?? [];
  const { page, setPage, totalPages, pageItems } = usePagedItems(items, pageSize);
  React.useEffect(() => {
    setPage(1);
  }, [pageSize, setPage]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <FieldHint className="mb-2">{t("coreInstances.help")}</FieldHint>
      {list.isLoading ? (
  <LoadingState />
) : list.isError ? (
  <ErrorState message={t("coreInstances.loadError")} onRetry={() => list.refetch()} />
) : items.length === 0 ? (
  <EmptyState icon={Inbox} message={t("coreInstances.empty")} />
) : (
  <>
  <div className="flex-1 min-h-0 overflow-auto">
  <Table>
            <THead>
              <TR>
                <TH>{t("coreInstances.col.tenant")}</TH>
                <TH>{t("coreInstances.col.label")}</TH>
                <TH>{t("coreInstances.col.callback")}</TH>
                <TH>{t("coreInstances.col.createdAt")}</TH>
                <TH>{t("coreInstances.col.lastSeenAt")}</TH>
                <TH>{t("coreInstances.col.status")}</TH>
                <TH></TH>
              </TR>
            </THead>
            <TBody>
              {pageItems.map((c) => {
                const revoked = !!c.revoked_at;
                return (
                  <TR key={c.id}>
                    <TD>
                      {c.tenant_code ? (
                        <div className="font-medium">{c.tenant_code}</div>
                      ) : (
                        <div className="font-mono text-xs">{c.tenant_id}</div>
                      )}
                    </TD>
                    <TD>{c.label || "—"}</TD>
                    <TD>{c.has_callback ? t("coreInstances.callback.yes") : t("coreInstances.callback.no")}</TD>
                    <TD className="text-xs">{fmt(c.created_at)}</TD>
                    <TD className="text-xs">
                      {c.last_seen_at ? fmt(c.last_seen_at) : t("coreInstances.never")}
                    </TD>
                    <TD>
                      {revoked ? (
                        <Badge variant="default">{t("coreInstances.status.revoked")}</Badge>
                      ) : (
                        <Badge variant="primary">{t("coreInstances.status.active")}</Badge>
                      )}
                    </TD>
                    <TD>
                      {!revoked && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setRevokeTarget(c.id)}
                        >
                          {t("coreInstances.revoke")}
                        </Button>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
  </div>
  <DataPagination
    page={page}
    totalPages={totalPages}
    pageSize={pageSize}
    onChange={setPage}
    onPageSizeChange={setPageSize}
  />
  </>
)}
      <RevokeCoreDialog
        id={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={async () => {
          if (!revokeTarget) return;
          await revoke.mutateAsync(revokeTarget);
        }}
        isPending={revoke.isPending}
      />
    </div>
  );
}

function ActivationCodeDialog({
  open,
  onOpenChange,
  form,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  form: ReturnType<typeof useActivationCodeForm>;
}) {
  const { t } = useTranslation();
  const fmtDateTime = useFormatUiDateTime();
  const {
    tenantCode,
    setTenantCode,
    tenantName,
    setTenantName,
    expiresHours,
    setExpiresHours,
    revokeExisting,
    setRevokeExisting,
    result,
    submit,
    reset,
  } = form;

  React.useEffect(() => {
    if (open) reset();
  }, [open]);

  const onConfirm = () => {
    submit.mutate();
  };
  const resultExpiresAt = result ? fmtDateTime(result.expires_at) : "";
  const resultCopyText = result
    ? [
        `${t("activation.resultTenant")}: ${result.tenant_code}`,
        `${t("activation.resultTenantId")}: ${result.tenant_id}`,
        `${t("activation.resultCode")}: ${result.code}`,
        `${t("activation.resultExpiresAt")}: ${resultExpiresAt}`,
      ].join("\n")
    : "";
  const copyText = async (text: string, successKey: string) => {
    try {
      await writeClipboard(text);
      toast.success(t(successKey));
    } catch {
      toast.error(t("activation.copyFailed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("activation.title")}</DialogTitle>
          <DialogDescription>{t("activation.help")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <RequiredLabel htmlFor="ac-tenant-code">{t("activation.tenantCode")}</RequiredLabel>
            <Input
              id="ac-tenant-code"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              placeholder="vd: acme-corp"
              autoFocus
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ac-tenant-name">{t("activation.tenantName")}</Label>
            <Input
              id="ac-tenant-name"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              placeholder="vd: ACME Corp"
            />
          </div>
          <div className="space-y-2">
            <RequiredLabel htmlFor="ac-expires">{t("activation.expiresIn")}</RequiredLabel>
            <Input
              id="ac-expires"
              type="number"
              min={1}
              value={expiresHours}
              onChange={(e) => setExpiresHours(Number(e.target.value) || 24)}
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="ac-revoke-existing"
              checked={revokeExisting}
              onCheckedChange={(checked) => setRevokeExisting(checked === true)}
            />
            <Label htmlFor="ac-revoke-existing" className="text-sm font-normal">
              {t("activation.revokeExisting")}
            </Label>
          </div>
          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold">{t("activation.resultTitle")}</div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(result.code, "activation.copyCodeSuccess")}
                    className="h-7 border-emerald-300 bg-white px-2 text-xs text-emerald-900 hover:bg-emerald-100"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {t("activation.copyCode")}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(resultCopyText, "activation.copyAllSuccess")}
                    className="h-7 border-emerald-300 bg-white px-2 text-xs text-emerald-900 hover:bg-emerald-100"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {t("activation.copyAll")}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <ActivationResultRow
                  label={t("activation.resultTenant")}
                  value={result.tenant_code}
                />
                <ActivationResultRow
                  label={t("activation.resultTenantId")}
                  value={result.tenant_id}
                />
                <ActivationResultRow
                  label={t("activation.resultCode")}
                  value={result.code}
                />
                <ActivationResultRow
                  label={t("activation.resultExpiresAt")}
                  value={resultExpiresAt}
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            {t("dialog.common.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={submit.isPending}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("activation.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useActivationCodeForm() {
  const { t } = useTranslation();
  const [tenantCode, setTenantCode] = React.useState("");
  const [tenantName, setTenantName] = React.useState("");
  const [expiresHours, setExpiresHours] = React.useState(24);
  const [revokeExisting, setRevokeExisting] = React.useState(false);
  const [result, setResult] = React.useState<{
    code: string;
    tenant_id: string;
    tenant_code: string;
    expires_at: string;
  } | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      if (!tenantCode.trim()) throw new Error(t("activation.needTenantCode"));
      const body: Record<string, unknown> = {
        tenant_code: tenantCode.trim(),
        expires_in_hours: Number(expiresHours) || 24,
        revoke_existing_core_instances: revokeExisting,
      };
      if (tenantName.trim()) body.tenant_name = tenantName.trim();
      return apiClient.post<{
        code: string;
        tenant_id: string;
        tenant_code: string;
        expires_at: string;
      }>("/api/v1/admin/activation-codes", body);
    },
    onSuccess: (res) => {
      setResult(res.data);
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  return {
    tenantCode,
    setTenantCode,
    tenantName,
    setTenantName,
    expiresHours,
    setExpiresHours,
    revokeExisting,
    setRevokeExisting,
    result,
    submit,
    reset: React.useCallback(() => {
      setTenantCode("");
      setTenantName("");
      setExpiresHours(24);
      setRevokeExisting(false);
      setResult(null);
      submit.reset();
    }, [submit]),
  };
}

function ManualConfirmDialog({
  open,
  onOpenChange,
  form,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  form: ReturnType<typeof useManualConfirmForm>;
}) {
  const { t } = useTranslation();
  const {
    tenantCode,
    setTenantCode,
    setTenantId,
    tenantInfo,
    setTenantInfo,
    targetType,
    setTargetType,
    appId,
    setAppId,
    planCode,
    setPlanCode,
    periodStart,
    setPeriodStart,
    seatCount,
    setSeatCount,
    orderRef,
    setOrderRef,
    tenants,
    apps,
    plans,
    submit,
    reset,
  } = form;
  const [tenantSearchOpen, setTenantSearchOpen] = React.useState(false);
  const tenantSearchRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (open) {
      reset();
      setTenantSearchOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!tenantSearchOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!tenantSearchRef.current?.contains(event.target as Node)) {
        setTenantSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [tenantSearchOpen]);

  const onConfirm = () =>
    submit.mutate(undefined, {
      onSuccess: (result) => {
        if (result.status !== "already") {
          onOpenChange(false);
        }
      },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("confirm.title")}</DialogTitle>
          <DialogDescription>{t("confirm.help")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <RequiredLabel htmlFor="mc-tenant-code">{t("confirm.tenantCode")}</RequiredLabel>
            <div ref={tenantSearchRef} className="relative">
              <Input
                id="mc-tenant-code"
                className="pr-10"
                value={tenantCode}
                onChange={(e) => {
                  setTenantCode(e.target.value);
                  setTenantId(null);
                  setTenantInfo("");
                  setTenantSearchOpen(true);
                }}
                onFocus={() => setTenantSearchOpen(true)}
                placeholder={t("confirm.tenantPlaceholder")}
                autoFocus
                required
              />
              <button
                type="button"
                className="absolute right-1 top-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => setTenantSearchOpen((prev) => !prev)}
                aria-label={t("confirm.tenantCode")}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              {tenantSearchOpen && (
                <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md">
                  {tenants.isLoading ? (
                    <div className="px-2 py-2 text-xs text-muted-foreground">
                      {t("apps.loading")}
                    </div>
                  ) : (tenants.data?.items?.length ?? 0) === 0 ? (
                    <div className="px-2 py-2 text-xs text-muted-foreground">
                      {t("confirm.tenantNotFound")}
                    </div>
                  ) : (
                    (tenants.data?.items ?? []).map((tenant) => (
                      <button
                        key={tenant.id}
                        type="button"
                        className="flex w-full flex-col rounded-sm px-2 py-2 text-left hover:bg-accent"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setTenantCode(tenant.code);
                          setTenantId(tenant.id);
                          setTenantInfo(
                            t("confirm.tenantFound", {
                              name: tenant.name || tenant.code,
                              code: tenant.code,
                            })
                          );
                          setTenantSearchOpen(false);
                        }}
                      >
                        <span className="text-sm font-medium">{tenant.name || tenant.code}</span>
                        <span className="text-xs text-muted-foreground">{tenant.code}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {tenantInfo && <FieldHint>{tenantInfo}</FieldHint>}
          </div>

          <div className="space-y-2">
            <RequiredLabel className="text-xs font-medium text-muted-foreground">
              {t("confirm.targetType")}
            </RequiredLabel>
            <RadioGroup
              value={targetType}
              onValueChange={(value) => setTargetType(value as "plan" | "app")}
              className="flex gap-4 text-sm"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem id="cf-target-plan" value="plan" />
                <Label htmlFor="cf-target-plan" className="text-sm font-normal">
                  {t("confirm.target.plan")}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="cf-target-app" value="app" />
                <Label htmlFor="cf-target-app" className="text-sm font-normal">
                  {t("confirm.target.app")}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {targetType === "plan" ? (
            <div className="space-y-2">
              <RequiredLabel htmlFor="mc-plan">{t("confirm.plan")}</RequiredLabel>
              <Select id="mc-plan" value={planCode} onChange={(e) => setPlanCode(e.target.value)} required>
                <option value="">—</option>
                {(plans.data?.items ?? []).map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name || p.code} ({p.code})
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <RequiredLabel htmlFor="mc-app">{t("confirm.app")}</RequiredLabel>
              <Select id="mc-app" value={appId} onChange={(e) => setAppId(e.target.value)} required>
                <option value="">—</option>
                {(apps.data?.items ?? []).map((a) => (
                  <option key={a.app_id} value={a.app_id}>
                    {a.name} ({a.app_id})
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <RequiredLabel htmlFor="mc-period-start">{t("confirm.periodStart")}</RequiredLabel>
              <Input
                id="mc-period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mc-seat">{t("confirm.seatCount")}</Label>
              <Input
                id="mc-seat"
                type="number"
                min={1}
                step={1}
                value={seatCount}
                onChange={(e) => setSeatCount(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <RequiredLabel htmlFor="mc-order">{t("confirm.orderRef")}</RequiredLabel>
            <Input
              id="mc-order"
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
              placeholder="vd: mã UNC chuyển khoản"
              required
            />
          </div>
          <FieldHint>{t("confirm.auditAuto")}</FieldHint>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            {t("dialog.common.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={submit.isPending}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("confirm.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useManualConfirmForm() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tenantCode, setTenantCode] = React.useState("");
  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = React.useState<string>("");
  const [targetType, setTargetType] = React.useState<"plan" | "app">("plan");
  const [appId, setAppId] = React.useState("");
  const [planCode, setPlanCode] = React.useState("");
  const [periodStart, setPeriodStart] = React.useState(
    new Date().toISOString().slice(0, 10)
  );
  const [seatCount, setSeatCount] = React.useState("");
  const [orderRef, setOrderRef] = React.useState("");

  const apps = useQuery({
    queryKey: ["admin", "apps", "all"],
    queryFn: () =>
      http.get<{ items: { app_id: string; name: string }[] }>(
        "/api/v1/admin/apps",
        { limit: 500 }
      ),
  });
  const plans = useQuery({
    queryKey: ["admin", "plans", "list"],
    queryFn: () =>
      http.get<{ items: { code: string; name: string | null }[] }>(
        "/api/v1/admin/plans"
      ),
  });
  const planCatalog = useQuery({
    queryKey: ["plans", "catalog", "single-app-resolver"],
    queryFn: () =>
      http.get<{ items: PlanCatalogItem[] }>("/api/v1/plans"),
  });

  const tenants = useQuery({
    queryKey: ["admin", "tenants", tenantCode],
    queryFn: () =>
      http.get<{ items: Tenant[] }>("/api/v1/admin/tenants", tenantCode.trim()
        ? {
            q: tenantCode.trim(),
            limit: 20,
          }
        : {
            limit: 20,
          }),
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error(t("confirm.needTenant"));
      if (targetType === "app" && !appId) throw new Error(t("confirm.needApp"));
      if (targetType === "plan" && !planCode) throw new Error(t("confirm.needPlan"));
      if (!orderRef.trim()) throw new Error(t("confirm.needAudit"));
      const seats = seatCount.trim() ? Number(seatCount) : null;
      if (seats != null && (!Number.isInteger(seats) || seats < 1)) {
        throw new Error(t("confirm.needSeatCount"));
      }
      const body: Record<string, unknown> = {
        tenant_id: tenantId,
        period_start: periodStart + "T00:00:00Z",
        source: "manual",
        order_ref: orderRef.trim(),
      };
      if (targetType === "plan") {
        body.plan_code = planCode;
      } else {
        const singleAppPlan = resolveSingleAppPlan(planCatalog.data?.items ?? [], appId);
        if (!singleAppPlan) {
          throw new Error(t("confirm.needSingleAppPlan"));
        }
        body.plan_code = singleAppPlan.code;
      }
      if (seats != null) body.seat_count = seats;
      const res = await apiClient.post<ConfirmEntitlementResult>(
        "/api/v1/entitlements/confirm",
        body
      );
      return res?.data ?? { status: "confirmed" as const };
    },
    onSuccess: (result) => {
      const selectedTarget =
        targetType === "plan"
          ? planCode
          : (resolveSingleAppPlan(planCatalog.data?.items ?? [], appId)?.code ?? appId);
      const target = result.target_id ?? selectedTarget;
      const message = t(result.status === "already" ? "confirm.already" : "confirm.success", {
        target,
      });
      if (result.status === "already") {
        toast.warning(message);
      } else {
        toast.success(message);
      }
      qc.invalidateQueries({ queryKey: ["admin", "tenant-subs"] });
    },
    onError: (e) => {
      toast.error(parseApiError(e));
    },
  });

  return {
    tenantCode,
    setTenantCode,
    setTenantId,
    tenantInfo,
    setTenantInfo,
    targetType,
    setTargetType,
    appId,
    setAppId,
    planCode,
    setPlanCode,
    periodStart,
    setPeriodStart,
    seatCount,
    setSeatCount,
    orderRef,
    setOrderRef,
    tenants,
    apps,
    plans,
    planCatalog,
    submit,
    reset: React.useCallback(() => {
      setTenantCode("");
      setTenantId(null);
      setTenantInfo("");
      setTargetType("plan");
      setAppId("");
      setPlanCode("");
      setPeriodStart(new Date().toISOString().slice(0, 10));
      setSeatCount("");
      setOrderRef("");
      submit.reset();
    }, [submit]),
  };
}
